/**
 * Family-side guided "Find Care" wizard — the single, low-stakes
 * entry-point the June 2026 audit recommended for Issue 18 (the family
 * dashboard is too dense for a stressed adult child or older adult).
 *
 * The flow is deliberately three soft questions, large tap targets, and
 * almost no jargon. At the end we route to the existing
 * /patient/find-clinicians page with prefilled filters in the query
 * string, so the actual search/booking funnel stays unchanged.
 *
 * The full PatientDashboard is still available via the "Manage everything"
 * link in the top-right.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import {
  Heart, Activity, Stethoscope, Brain, Sparkles, ShieldCheck, ArrowRight, ArrowLeft,
  Clock, MapPin, Loader2,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEEDS = [
  { id: 'wound_care', label: 'Wound care or dressing changes', icon: Activity, service: 'skilled_nursing' },
  { id: 'medication', label: 'Medication help & reminders', icon: Heart, service: 'in_home_care' },
  { id: 'companion', label: 'Companion care', icon: Sparkles, service: 'companion_care' },
  { id: 'dementia', label: 'Dementia or memory care', icon: Brain, service: 'in_home_care' },
  { id: 'post_surgery', label: 'Post-surgery recovery', icon: Stethoscope, service: 'post_discharge' },
  { id: 'chronic', label: 'Help with a chronic condition', icon: Heart, service: 'chronic_care' },
  { id: 'hospice', label: 'Hospice or palliative comfort', icon: ShieldCheck, service: 'hospice_palliative' },
  { id: 'unsure', label: 'I’m not sure yet', icon: Sparkles, service: null },
];

const URGENCIES = [
  { id: 'today', label: 'Today, please', hint: 'A clinician within 24 hours' },
  { id: 'week', label: 'This week', hint: 'Within the next 7 days' },
  { id: 'month', label: 'This month', hint: 'Within the next 30 days' },
  { id: 'unsure', label: 'Just exploring', hint: 'I want to learn what’s out there' },
];

const URGENCY_LABELS = { today: 'Today, please', week: 'This week', month: 'This month', unsure: 'Just exploring' };
const RECENTS_STORAGE_KEY = 'caraloom_findcare_recents_v1';

function loadRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENTS_STORAGE_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter(Boolean).slice(0, 2) : [];
  } catch {
    return [];
  }
}

function saveRecent(entry) {
  try {
    const existing = loadRecents();
    const dedup = existing.filter((r) => !(r.need === entry.need && r.urgency === entry.urgency && r.zip === entry.zip));
    const next = [entry, ...dedup].slice(0, 2);
    localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch { /* localStorage unavailable; degrade silently */ }
}

export default function PatientFindCare() {
  const { user, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [need, setNeed] = useState(null);
  const [urgency, setUrgency] = useState(null);
  const [zip, setZip] = useState('');
  const [busy, setBusy] = useState(false);
  const [recents, setRecents] = useState(() => loadRecents());

  // Pre-fill ZIP from the patient profile if we already know it.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/patient/profile`, getAuthHeader())
      .then((r) => { if (r.data?.zip_code) setZip(String(r.data.zip_code)); })
      .catch(() => {});
  }, [token, getAuthHeader]);

  const buildSearchParams = (n, u, z) => {
    const params = new URLSearchParams();
    const chosenNeed = NEEDS.find((nx) => nx.id === n);
    if (chosenNeed?.service) params.set('service', chosenNeed.service);
    if (chosenNeed?.label) params.set('q', chosenNeed.label);
    if (u) {
      const urgencyMap = { today: 'urgent', week: 'this_week', month: 'this_month', unsure: 'exploring' };
      params.set('urgency', urgencyMap[u] || u);
    }
    if (z) params.set('zip', z);
    return params;
  };

  const runRecent = (entry) => {
    // One-tap rerun — does not show the wizard at all.
    const params = buildSearchParams(entry.need, entry.urgency, entry.zip);
    navigate(`/patient/find-clinicians?${params.toString()}`);
  };

  const finish = async () => {
    setBusy(true);
    saveRecent({ need, urgency, zip, savedAt: new Date().toISOString() });
    const params = buildSearchParams(need, urgency, zip);
    navigate(`/patient/find-clinicians?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cn-bone-100 via-white to-cn-bone-100 dark:from-cn-ink-950 dark:via-cn-ink-900 dark:to-cn-ink-950" data-testid="find-care-wizard">
      {/* Soft, low-density header — no sidebar on purpose */}
      <header className="cn-glass border-b border-cn-border">
        <div className="cn-container h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <CaraLoomLogo />
          </Link>
          <Link to="/patient/dashboard" className="text-sm text-cn-text-muted hover:text-cn-primary" data-testid="find-care-manage-everything">
            Manage everything &rarr;
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-12 pb-16">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="find-care-progress">
          {[1, 2, 3].map((s) => (
            <span key={s} className={`h-2 rounded-full transition-all ${step === s ? 'w-10 bg-cn-primary' : 'w-2 bg-cn-border'}`} />
          ))}
        </div>

        {step === 1 && (
          <section data-testid="find-care-step-1">
            {recents.length > 0 && (
              <div className="mb-8" data-testid="find-care-recents">
                <p className="text-xs uppercase tracking-wider text-cn-text-muted font-bold mb-3">Find again — one tap</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {recents.map((entry, idx) => {
                    const needMeta = NEEDS.find((n) => n.id === entry.need);
                    const Icon = needMeta?.icon || Sparkles;
                    return (
                      <button
                        key={`${entry.need}-${entry.urgency}-${entry.zip}-${idx}`}
                        onClick={() => runRecent(entry)}
                        data-testid={`find-care-recent-${idx}`}
                        className="flex items-center gap-3 p-4 rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 hover:border-cn-primary/50 hover:bg-cn-primary/[0.02] text-left transition-all group"
                      >
                        <div className="w-11 h-11 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-cn-text dark:text-white truncate">{needMeta?.label || 'Recent search'}</p>
                          <p className="text-[11px] text-cn-text-muted mt-0.5 truncate">
                            {URGENCY_LABELS[entry.urgency] || entry.urgency}
                            {entry.zip ? ` · ${entry.zip}` : ''}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-cn-text-muted group-hover:text-cn-primary transition-colors" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-cn-text-muted text-center mt-4">Or start a new search below.</p>
              </div>
            )}
            <p className="text-sm uppercase tracking-wider text-cn-primary font-bold text-center mb-2">Step 1 of 3</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-center text-cn-text dark:text-white">
              Hi {user?.first_name || 'there'}. What kind of care does your loved one need?
            </h1>
            <p className="text-center text-cn-text-muted mt-3 mb-8">
              Pick whatever feels closest. You can change this later.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {NEEDS.map((n) => {
                const Icon = n.icon;
                const active = need === n.id;
                return (
                  <button
                    key={n.id}
                    onClick={() => setNeed(n.id)}
                    data-testid={`find-care-need-${n.id}`}
                    className={`flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                      active
                        ? 'bg-cn-primary text-white border-cn-primary shadow-md shadow-cn-primary/20'
                        : 'bg-white dark:bg-cn-ink-800 border-cn-border hover:border-cn-primary/40'
                    }`}
                  >
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${active ? 'bg-white/15 text-white' : 'bg-cn-primary/10 text-cn-primary'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`font-medium text-sm ${active ? 'text-white' : 'text-cn-text dark:text-white'}`}>{n.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-8">
              <Button onClick={() => setStep(2)} disabled={!need} className="cn-btn cn-btn-primary" data-testid="find-care-step-1-next">
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section data-testid="find-care-step-2">
            <p className="text-sm uppercase tracking-wider text-cn-primary font-bold text-center mb-2">Step 2 of 3</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-center text-cn-text dark:text-white">
              When do you need care?
            </h1>
            <p className="text-center text-cn-text-muted mt-3 mb-8">
              We’ll prioritize clinicians who are available in that window.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {URGENCIES.map((u) => {
                const active = urgency === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => setUrgency(u.id)}
                    data-testid={`find-care-urgency-${u.id}`}
                    className={`flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all ${
                      active
                        ? 'bg-cn-primary text-white border-cn-primary shadow-md shadow-cn-primary/20'
                        : 'bg-white dark:bg-cn-ink-800 border-cn-border hover:border-cn-primary/40'
                    }`}
                  >
                    <Clock className={`w-6 h-6 shrink-0 ${active ? 'text-white' : 'text-cn-primary'}`} />
                    <div>
                      <p className={`font-bold ${active ? 'text-white' : 'text-cn-text dark:text-white'}`}>{u.label}</p>
                      <p className={`text-xs ${active ? 'text-white/80' : 'text-cn-text-muted'}`}>{u.hint}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-8">
              <Button onClick={() => setStep(1)} variant="ghost" className="text-cn-text-muted" data-testid="find-care-step-2-back">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!urgency} className="cn-btn cn-btn-primary" data-testid="find-care-step-2-next">
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section data-testid="find-care-step-3">
            <p className="text-sm uppercase tracking-wider text-cn-primary font-bold text-center mb-2">Step 3 of 3</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-center text-cn-text dark:text-white">
              Where should we look?
            </h1>
            <p className="text-center text-cn-text-muted mt-3 mb-8">
              Enter your ZIP code so we can show clinicians who serve your area.
            </p>
            <div className="max-w-sm mx-auto">
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cn-primary" />
                <Input
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="ZIP code"
                  inputMode="numeric"
                  className="pl-12 h-14 rounded-2xl text-lg font-medium bg-white border-cn-border"
                  data-testid="find-care-zip-input"
                />
              </div>
              <p className="text-xs text-cn-text-muted text-center mt-3">
                We use your ZIP to compute real distances — we never share it with clinicians until you book.
              </p>
            </div>
            <div className="flex items-center justify-between mt-8">
              <Button onClick={() => setStep(2)} variant="ghost" className="text-cn-text-muted" data-testid="find-care-step-3-back">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              <Button onClick={finish} disabled={busy || zip.length !== 5} className="cn-btn cn-btn-primary" data-testid="find-care-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Find my clinicians <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </div>
          </section>
        )}

        <p className="text-center text-xs text-cn-text-muted mt-12">
          Every clinician on CaraLoom is license-verified · payments are held until your visit is verified.
        </p>
      </main>
    </div>
  );
}
